import { Component, CSSProperties, ErrorInfo, ReactNode, memo } from "react";
import { dashboardWidgetMapper } from "./dashboardWidgetMapper";
import { DashboardWidget } from "./types";

/** Translate a widget layout into CSS grid placement. */
const layoutToGridStyle = (widget: DashboardWidget, singleColumn = false): CSSProperties => {
    const { col, row, colSpan = 1, rowSpan = 1 } = widget.layout ?? {};
    if (singleColumn) {
        // Single-column (mobile) reflow: ignore the configured column start/span
        // and explicit row so every widget stacks full-width in document order.
        // `rowSpan` is kept so a widget's intended height still drives its rows.
        return {
            gridColumn: "1 / -1",
            gridRow: `span ${rowSpan}`,
        };
    }
    return {
        gridColumn: col != null ? `${col} / span ${colSpan}` : `span ${colSpan}`,
        gridRow: row != null ? `${row} / span ${rowSpan}` : `span ${rowSpan}`,
    };
};

interface WidgetErrorBoundaryProps {
    uid: string;
    children: ReactNode;
}

/** Isolates a single widget so a render error doesn't take down the whole dashboard. */
class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, { hasError: boolean }> {
    constructor(props: WidgetErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // eslint-disable-next-line no-console
        console.error(`Dashboard widget "${this.props.uid}" crashed:`, error, info);
    }

    render() {
        if (this.state.hasError) {
            return <div className="DashboardWidget__error">This widget failed to render.</div>;
        }
        return this.props.children;
    }
}

// Memoized so a re-render of an ancestor (RouterManager re-renders on many store
// dispatches; its RouterContext value is kept stable, but the dashboard subtree
// itself carries no memo boundary) does NOT cascade a full re-render into every
// widget's heavy MUIDataTable. `widget` is a stable reference from DashboardView's
// memoized `widgets` array and `singleColumn` is a boolean, so the default shallow
// prop compare holds while nothing about this widget changed — the tables then
// only re-render on genuine data changes (the widget's own polled state) or a real
// context change. This is the second half of the intended pattern (stable context
// value + memoized consumer) and the fix for the operator-dashboard CPU burn
// (operator-task-operator-dashboard-cpu-render-loop).
export const DashboardWidgetFrame = memo(function DashboardWidgetFrame({
    widget,
    singleColumn = false,
}: {
    widget: DashboardWidget;
    singleColumn?: boolean;
}) {
    const WidgetComponent = dashboardWidgetMapper[widget.type];
    return (
        <section className="DashboardWidget" style={layoutToGridStyle(widget, singleColumn)}>
            {widget.title && <header className="DashboardWidget__title">{widget.title}</header>}
            <div className="DashboardWidget__body">
                {WidgetComponent ? (
                    <WidgetErrorBoundary uid={widget.uid}>
                        <WidgetComponent widget={widget} config={widget.config ?? {}} />
                    </WidgetErrorBoundary>
                ) : (
                    <div className="DashboardWidget__error">
                        Unknown widget type: <code>{widget.type}</code>
                    </div>
                )}
            </div>
        </section>
    );
});
